import { useState, useMemo, useEffect } from 'react'
import axios from 'axios'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { API_BASE_URL } from '../constants/system'
import useAccount from './useAccount'
import useLoading from './useLoading'
import { extractErrorMessage } from '../utils/apitools'

import {
  MultiTableConfigTable,
  MultiTableConfig,
  MultiTable,
} from '../types/gotypes'

import {
  getAugementedTOC,
  normalizeTablePath,
  arePathsEqual,
} from '../utils/multitables'

const buildFullPath = (table: MultiTableConfigTable) => {
  if (!table) return ''
  return table.folderPath ? `${table.folderPath}/${table.filename}` : table.filename
}

export const useMultiTable = ({
  customer,
  job,
  multiTableId,
  table,
}: {
  customer: string
  job: string
  multiTableId: string
  table: string
}) => {
  const account = useAccount()
  const loading = useLoading()
  const queryClient = useQueryClient()
  const [saveError, setSaveError] = useState<string | null>(null)
  const [tables, setTables] = useState<MultiTableConfigTable[]>([])

  const multiTablesQueryKey = useMemo(() => {
    return ['multiTables', customer, job]
  }, [customer, job])

  const loadQuery = useQuery({
    queryKey: multiTablesQueryKey,
    enabled: Boolean(customer && job && account.user),
    queryFn: async () => {
      const response = await axios.get<{ multitables: MultiTable[] }>(`${API_BASE_URL}/multitables/${customer}/${job}`)
      const tables = response.data.multitables || []
      return tables
    },
  })

  const error = saveError || loadQuery.error
  const multitables = loadQuery.data
  const multiTable = useMemo(() => {
    if(multiTableId == 'new') {
      const data: MultiTable = {
        id: 'new',
        created_at: Date.now(),
        updated_at: Date.now(),
        customer,
        job,
        name: '',
        user: '',
        config: {
          tables: [],
          name: '',
          description: '',
          global_filter: '',
        },
      }
      return data
    }
    return multitables?.find(table => table.id === multiTableId)
  }, [multitables, multiTableId])

  const augmentedToc = useMemo(() => {
    return getAugementedTOC({
      toc: account.toc || [],
      multiTables: multitables || [],
    })
  }, [account.toc, multitables])

  const isInMultiTable = (tablePath: string) => {
    return tables.some(table => arePathsEqual(buildFullPath(table), tablePath))
  }

  const saveMultiTable = async (payload: MultiTable) => {
    loading.setLoading(true)
    setSaveError(null)

    try {
      if (payload.id === 'new') {
        const response = await axios.post<MultiTable>(`${API_BASE_URL}/multitables/${customer}/${job}/`, {
          config: payload.config,
        })
        await account.reloadTOC()
        queryClient.invalidateQueries({ queryKey: multiTablesQueryKey })
        const fallbackTable = response.data.config?.tables?.[0]
        const activePath = fallbackTable ? buildFullPath(fallbackTable) : ''
        account.openPage('multitables', {
          multiTableId: response.data.id,
          table: normalizeTablePath(activePath, true),
        })
      } else {
        await axios.put<MultiTable>(`${API_BASE_URL}/multitables/${customer}/${job}/${payload.id}`, {
          config: payload.config,
        })
        await account.reloadTOC()
        queryClient.invalidateQueries({ queryKey: multiTablesQueryKey })
      }
    } catch (err) {
      const message = extractErrorMessage(err)
      setSaveError(message)
      throw err
    } finally {
      loading.setLoading(false)
    }
  }

  const deleteMultiTable = async (id: string) => {
    loading.setLoading(true)
    try {
      await axios.delete(`${API_BASE_URL}/multitables/${customer}/${job}/${id}`)
      await account.reloadTOC()
      queryClient.invalidateQueries({ queryKey: multiTablesQueryKey })
    } catch (err) {
      const message = extractErrorMessage(err)
      setSaveError(message)
      throw err
    } finally {
      loading.setLoading(false)
    }
  }

  useEffect(() => {
    if(!multiTable) return
    if(multiTableId == 'new') return
    setTables(multiTable?.config?.tables || [])
  }, [multiTable])

  return {
    multitables,
    multiTable,
    augmentedToc,
    tables,
    setTables,
    isInMultiTable,
    saveMultiTable,
    deleteMultiTable,
    error,
  }
}

export default useMultiTable
